import { IPromotion, PromotionType } from './Promotion';
import {
	GetLocationsResponse,
	GetLocationsResponseKr,
	locationsResponse,
} from '@andromeda/polaris-data-access-products';
import { trimText, getDeviceType } from '@/utils';
import { Takeover } from '@/takeover';
import { PromotionCarouselItem } from './PromotionCarouselItem';
import RetailerOffer from './RetailerOffer';
import { Partner } from './Partner';

export default class MxrRetailer {
	public AccessPointId: string;

	public FulfillmentStoreId: string;

	public LocationId: string;

	public Street: string;

	public City: string;

	public State: string;

	public ZipCode: string;

	public Distance: string;

	public DistanceUnit: string;

	public Offers: RetailerOffer[];

	public Promotions: PromotionCarouselItem[];

	public CarouselIndexItemId: Map<number, number>;

	public Chain: string;

	public Logo: string;

	public StoreUrl: string;

	public Partner: Partner;

	public Preformatted: Boolean;

	constructor(
		accessPointId: string,
		fulfillmentStoreId: string,
		street: string,
		city: string,
		state: string,
		zipCode: string,
		distance: string,
		distanceUnit: string,
		offers: RetailerOffer[],
		promotions: PromotionCarouselItem[],
		carouselIndexItemId: Map<number, number>,
		chain: string,
		logo: string,
		storeUrl: string,
		partner: Partner,
		preformatted?: Boolean
	) {
		this.AccessPointId = accessPointId;
		this.FulfillmentStoreId = fulfillmentStoreId;
		this.LocationId = fulfillmentStoreId;
		this.Street = street;
		this.City = city;
		this.State = state;
		this.ZipCode = zipCode;
		this.Distance = distance;
		this.DistanceUnit = distanceUnit;
		this.Offers = offers;
		this.Promotions = promotions;
		this.CarouselIndexItemId = carouselIndexItemId;
		this.Chain = chain;
		this.Logo = logo || '';
		this.StoreUrl = storeUrl || '';
		this.Partner = partner;
		this.Preformatted = preformatted || false;
	}

	get FormattedDistance() {
		let distance = `${this.Distance}`;
		if (this.DistanceUnit.length) {
			if (this.DistanceUnit[0].toLowerCase() === 'm') {
				distance += 'mi';
			} else {
				distance += 'km';
			}
		}
		return distance;
	}

	/**
	 * Get stores brand(s) for partners
	 * Returns the brands in the array based on preference (first is better than last)
	 */
	get StoreBrands(): string[] {
		switch (this.Partner) {
			case Partner.WALMART:
				return ['great value', 'gv'];
			case Partner.KROGER:
				return this.getKroStoreBrandPriority(this.Chain);
			default:
				return [];
		}
	}

	private getKroStoreBrandPriority(chain: string) {
		const chainName = chain.toLowerCase();
		const defaultPriority = ['simple truth', 'private selection', 'kroger'];
		const chainsBrandPriority = [
			[['kroger'], ['kroger', 'simple truth', 'private selection']],
			[
				['food 4 less', 'qfc', 'fred myers'],
				['simple truth', 'private selection', 'kroger'],
			],
		];
		const priority = chainsBrandPriority
			// Get the cbp that has the chain
			.filter((cbp) => cbp[0].indexOf(chainName) >= 0)
			// Get the brand priority from there
			.map((cbp) => cbp[1])
			// Take only one
			.reduce((a, b) => {
				if (b.length) {
					return b;
				}
				return a;
			}, []);
		return priority.length ? priority : defaultPriority;
	}

	private getItemIdByCarouselIndex = (carouselIndex: number): number => {
		const id = this.CarouselIndexItemId.get(carouselIndex);
		return id || -1;
	};

	public SetOffers(offers: RetailerOffer[]): void {
		const offerIds = new Set<string>();

		this.Offers = offers
			.filter((ro) => {
				if (offerIds.has(ro.Id)) {
					return false;
				}
				offerIds.add(ro.Id);
				return true;
			})
			.sort((ra, rb) => {
				if (!this.StoreBrands.length) return 0;
				const isRaStoreBrand = this.StoreBrands.filter(
					(sb) => ra.Name.toLowerCase().indexOf(sb) >= 0
				);
				const isRbStoreBrand = this.StoreBrands.filter(
					(sb) => rb.Name.toLowerCase().indexOf(sb) >= 0
				);
				if (isRaStoreBrand.length === isRbStoreBrand.length) {
					// No store brands
					if (isRaStoreBrand.length === 0) return 0;
					// Same store brand
					if (isRaStoreBrand[0] === isRbStoreBrand[0]) return 0;
					// Preferred Store Brand based on position in array
					for (let i = 0; i < this.StoreBrands.length; i += 1) {
						if (isRaStoreBrand[0] === this.StoreBrands[i]) {
							return -1;
						}
						if (isRbStoreBrand[0] === this.StoreBrands[i]) {
							return 1;
						}
					}
					// If we somehow made it here we have a logic issue so just return 0
					return 0;
				}
				if (!isRaStoreBrand.length && isRbStoreBrand.length) return 1;
				return -1;
			});
	}

	public SetPromotions(promotions: PromotionCarouselItem[]): void {
		this.Promotions = promotions;
		for (let i = 0; i < this.Promotions.length; i++) {
			const pi = this.Promotions[i];

			// Use this to hold the itemId and how many times it has been
			// added to the caoursel. If the maxOffers is 3 and there are
			// 3 disticing itemIds for this carousel we want to make sure
			// we take one offer from each itemId
			const itemIdsSetInCarousel = new Map<string, number>();

			// fill the itemIdsSetInCarousel with offer itemIds so we
			// will know how many offers we can take from each itemId
			this.Offers.filter((ro) => pi.itemIds.has(ro.ItemId)).forEach((i) =>
				itemIdsSetInCarousel.set(i.ItemId, 0)
			);

			pi.promotions = this.Offers
				// Filter by offers that have the itemIds for this carousel
				.filter((ro) => pi.itemIds.has(ro.ItemId))
				// Sort offers by store brand first
				.sort((ra, rb) => {
					if (!this.StoreBrands.length) return 0;
					const isRaStoreBrand = this.StoreBrands.filter(
						(sb) => ra.Name.toLowerCase().indexOf(sb) >= 0
					);
					const isRbStoreBrand = this.StoreBrands.filter(
						(sb) => rb.Name.toLowerCase().indexOf(sb) >= 0
					);
					if (isRaStoreBrand.length === isRbStoreBrand.length) {
						// No store brands
						if (isRaStoreBrand.length === 0) return 0;
						// Same store brand
						if (isRaStoreBrand[0] === isRbStoreBrand[0]) return 0;
						// Preferred Store Brand based on position in array
						for (let i = 0; i < this.StoreBrands.length; i += 1) {
							if (isRaStoreBrand[0] === this.StoreBrands[i]) {
								return -1;
							}
							if (isRbStoreBrand[0] === this.StoreBrands[i]) {
								return 1;
							}
						}
						// If we somehow made it here we have a logic issue so just return 0
						return 0;
					}
					if (!isRaStoreBrand.length && isRbStoreBrand.length)
						return 1;
					return -1;
				})
				// Take only the max amount of offers allowed per carousel
				// Making sure to evenly split them amongst itemIds
				.filter((ro) => {
					// No base ingredients but make sure we still only tax maxOffers
					if (pi.itemIds.size === 0) {
						const itemsSoFar = itemIdsSetInCarousel.get('default');
						if (!itemsSoFar) {
							itemIdsSetInCarousel.set('default', 1);
							return true;
						}
						if (itemsSoFar >= pi.maxOffers) {
							return false;
						}
						itemIdsSetInCarousel.set('default', itemsSoFar + 1);
						return true;
					}
					const itemSet = itemIdsSetInCarousel.get(ro.ItemId);
					const maxPerItemId = Math.ceil(
						pi.maxOffers / itemIdsSetInCarousel.size
					);
					if (itemSet) {
						if (itemSet >= maxPerItemId) return false;
						itemIdsSetInCarousel.set(ro.ItemId, itemSet + 1);
						return true;
					}
					itemIdsSetInCarousel.set(ro.ItemId, 1);
					return true;
				})
				// Make sure we only take 3, somehow more get through...
				// perhaps at a later date test the previous filter to fix
				.filter((ro, i) => i < pi.maxOffers)
				.map(
					(ro) =>
						({
							id: ro.Id,
							price: ro.Price || '',
							productName: this.setProductName(ro.Name),
							saleEndDate: '',
							type: PromotionType.OLO,
							productDescription: '',
							productImage: ro.Image,
							productUpc: ro.Upc,
							itemId: ro.ItemId || '',
							uniqueId: Math.random(),
						} as IPromotion)
				);
			if (pi.promotions.length === 0) {
				pi.promotions = pi.defaultPromotions;
			}
		}
	}

	public static getLocationsResponse(
		resp: locationsResponse,
		takeover: Takeover,
		retailer: string
	): MxrRetailer[] {
		let r: MxrRetailer[] = [];
		switch (retailer) {
			case 'kroger':
				r = MxrRetailer.FromMercuryGetLocationsResponseKr(
					resp as GetLocationsResponseKr,
					takeover
				);
				break;
			case 'target':
				r = MxrRetailer.FromMercuryGetLocationsResponseTgt(
					resp as locationsResponse,
					takeover
				);
				break;
			default:
				r = MxrRetailer.FromMercuryGetLocationsResponse(
					resp as GetLocationsResponse,
					takeover
				);
				break;
		}
		return r;
	}

	public static FromMercuryGetLocationsResponse(
		resp: GetLocationsResponse,
		takeover: Takeover
	): MxrRetailer[] {
		return resp.locations
			? resp.locations.map(
					(ms) =>
						new MxrRetailer(
							ms.accessPointId,
							ms.fulfillmentStoreId,
							ms.location.address,
							ms.location.city,
							ms.location.state,
							ms.location.zipCode,
							ms.location.distance,
							'MI',
							[],
							[],
							takeover.CarouselIndexItemId,
							takeover.Name,
							'',
							'',
							takeover.Retailer
						)
			  )
			: [];
	}

	public static FromMercuryGetLocationsResponseTgt(
		resp: locationsResponse,
		takeover: Takeover
	): MxrRetailer[] {
		if (!resp) {
			return [];
		}
		return resp.locations.map(
			(ms) =>
				new MxrRetailer(
					ms.locationId,
					ms.locationId,
					ms.address.line1,
					ms.address.city,
					ms.address.state,
					ms.address.zipCode,
					ms.address.distance,
					'MI',
					[],
					[],
					takeover.CarouselIndexItemId,
					ms.chain,
					ms.logo,
					ms.url,
					takeover.Retailer,
					ms.preformatted
				)
		);
	}

	public static FromMercuryGetLocationsResponseKr(
		resp: GetLocationsResponseKr,
		takeover: Takeover
	): MxrRetailer[] {
		if (!resp) {
			return [];
		}
		return resp.locations.map(
			(ms) =>
				new MxrRetailer(
					ms.locationId,
					ms.locationId,
					ms.address.line1,
					ms.address.city,
					ms.address.state,
					ms.address.zipCode,
					ms.address.distance,
					'MI',
					[],
					[],
					takeover.CarouselIndexItemId,
					ms.chain,
					ms.logo,
					ms.url,
					takeover.Retailer,
					ms.preformatted
				)
		);
	}

	public static CreateDefault(): MxrRetailer {
		return new MxrRetailer(
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			[],
			[],
			new Map<number, number>(),
			'',
			'',
			'',
			Partner.NONE
		);
	}

	private setProductName(name: string): string {
		if (!name) {
			return '';
		}
		const deviceType = getDeviceType(window);
		const text =
			deviceType !== 'desktop' ? trimText(name, 45) : trimText(name, 50);
		return text;
	}
}
